<template>
  <div id="app">
    <!-- 
            Router view will contain one of the following
            Auth.vue 
            Public.vue
            Browser.vue
            PostEditor.vue
            ProjectEditor.vue
            QuestionnaireEditor.vue
        -->
    <router-view :key="key"></router-view>
    <!-- Global Snackbar -->
    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" :timeout="snackbar.timeout">
      <div class="d-flex align-center ga-3">
        <v-icon v-if="snackbar.icon" :icon="snackbar.icon" class="text-h5"></v-icon>
        <span v-html="snackbar.message"></span>
      </div>
      <template v-slot:actions>
        <v-btn v-if="snackbar.showClose" variant="text" @click="snackbar.visible = false">
          {{ $t(`common.actions.close`) }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
  import config from "@/config";
  import { useTheme } from "vuetify";
  import { snackbar } from "@/store/snackbar";
  import { useHead } from "@unhead/vue";
  import { useI18n } from "vue-i18n";
  import { useRoute } from "vue-router";

  /* eslint-disable max-len */
  export default {
    name: "app",
    setup() {
      const { t } = useI18n();
      const route = useRoute();

      useHead({
        titleTemplate: (title) => {
          const translationReady = t("meta.app") !== "meta.app";
          if (!translationReady) {
            // If translations are not ready yet, show generic title
            return `%s${title ? " | " : ""}Maptionnaire`;
          }
          return `%s${title ? " | " : ""}${t("meta.app")}`;
        },
        title: () => {
          const translationReady = t("meta.app") !== "meta.app";
          if (!translationReady) {
            // If translations are not ready yet, show generic "loading" text in english.
            // This is also WCAG compliant as screen readers will read this and user will know site is loading
            // (just translations are loading tho, but still enough of an indication)
            return "Loading...";
          }
          const name = route.meta?.titleKey ? t(route.meta.titleKey) : null;
          // check if there are more meta.titleKey in the matched routes (from parent routes)
          if (route.matched.length > 0) {
            for (let i = route.matched.length - 1; i >= 0; i--) {
              const record = route.matched[i];
              if (record.meta && record.meta.titleKey) {
                // add each parent titleKey after the found name, separated by " | "
                const parentTitle = t(record.meta.titleKey);
                if (parentTitle && parentTitle !== name) {
                  return `${name} | ${parentTitle}`;
                }
              }
            }
          }

          return name;
        },
      });

      return { snackbar };
    },
    mounted: function () {
      // set vantaa theme by checking if their translations are active (bit of a hacky hack but it works)
      const theme = useTheme();
      theme.global.name.value = config.vantaaTranslations ? "vantaaTheme" : "mptTheme";

      // Chunk cache busting + lazy loading means that right after a deploy, users who have not
      // reloaded since the deploy, will get errors since the chunks they are trying to load
      // have disappeared.
      //
      // See:
      // https://stackoverflow.com/questions/59385650/vuejs-browser-caching-and-loading-chunk-failed-issue
      // https://blog.francium.tech/vue-lazy-routes-loading-chunk-failed-9ee407bbd58
      //
      // This has the potential for an infinite loop :-(. TODO: figure out how to SENTRY if this
      // is looping
      //
      const helsinkiWfsGeoJsonErr = `Uncaught SyntaxError: Unexpected token '<', "<?xml vers"... is not valid JSON`; // https://github.com/Mapita/maptionnaire2/issues/1282
      const cacheBustingError = "unexpected token '<'";
      window.onerror = function (mMsg) {
        const message =
          "The app has been updated, please reload the page to get the latest changes.";
        if (mMsg.indexOf(helsinkiWfsGeoJsonErr) > -1) {
          console.log(
            `Caught error "${mMsg}". This is ignored because City of Helsinki WFS server causes it when you don't specify layer id.`
          );
        } else if (mMsg.toLowerCase().indexOf(cacheBustingError) > -1) {
          if (navigator.onLine) {
            /* eslint no-alert: "off" */
            if (window.confirm(message)) {
              window.location.reload();
            }
          }
        }
      };
    },
    data: function () {
      return {
        router: this.$route,
      };
    },
    computed: {
      key: function () {
        let key = this.$route.path;
        const isProjectEditor = this.$router?.currentRoute?.value?.matched?.some(
          (record) => record.meta.isProjectEditor
        );
        if (isProjectEditor) {
          key = this.$route.params.projectId;
        }
        const isQuestionnaireEditor = this.$router?.currentRoute?.value?.matched?.some(
          (record) => record.meta.isQuestionnaireEditor
        );
        if (isQuestionnaireEditor) {
          key = this.$route.params.questionnaireId;
        }
        return key;
      },
    },
  };
</script>

<style lang="scss">
  @import "@mdi/font/css/materialdesignicons.css";
  // Import scss
  @import "@/styles/index.scss";

  // Gloal app settings
  #app {
    font-family: #{$font-family};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: $text-color-dark;
  }

  // Support for apple's dynamic system font size
  // ARGRGRG Safari on macOS started to support this, meaning that text size is all
  // messed up on desktop. Need to figure out a reasonable "@support not" or other way
  // of limiting this to mobile (max size won't do since iPhones are too wide, also
  // supporting landscape gets tricky)
  // @supports (font: -apple-system-body) {
  //     html {
  //         font: -apple-system-body;
  //     }
  // }

  @media print {
    html,
    body {
      ::-webkit-scrollbar {
        display: none;
      }
    }
  }

  label.label,
  p.label {
    margin-bottom: 0.5rem;
  }
  legend.label {
    display: block;
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  // Survey notification relates CSS
  .mpt-survey-notification {
    display: flex;
    text-align: center;
    align-items: center;
    justify-content: space-between;
    height: $public-menu-height + 10px;
    background: #ffd6af;
  }
  .mpt-survey-notification .mpt-message {
    display: flex;
    flex-direction: column;
  }
  .mpt-survey-notification .mpt-message a {
    font-weight: 800;
    text-decoration: underline;
  }
  .mpt-survey-notification .mpt-do-not-button {
    margin: 3px auto 0 auto;
    padding: 0;
    height: 1rem;
  }
  .mpt-survey-notification button {
    margin: 0 5px;
  }
  .mpt-survey-notification .button.mpt-do-not-button:hover {
    background: transparent;
  }
  .mpt-survey-notification .button.mpt-close-button:hover {
    background: rgba(0, 0, 0, 0.1);
  }

  // Global overrides for Bulma elements

  // Hide right-hand vertical scrollbar added by bulma
  // NOTE NOTE NOTE NOTE
  // This was inducing horrible, horrible map scrolling behavior for the map container
  // TODO re-check if bulma scrollbars are present, and is yes, figure out a better way
  // to disable them.
  // html, body {
  //     overflow-y: unset;
  // }

  [dir="rtl"] .b-checkbox.checkbox .control-label {
    padding-right: calc(0.75em - 1px);
    padding-left: unset;
  }

  [dir="rtl"] .field.is-grouped > .control {
    margin-right: unset;
    margin-left: 0.75em;
  }

  [dir="rtl"] .control-label {
    padding-left: unset;
    padding-right: calc(0.75em - 1px);
  }

  [dir="rtl"] .slider-label-right,
  .range-label-right {
    margin-left: unset;
    margin-right: 15px;
  }
  [dir="rtl"] .mpt-button-icon.fa-layers {
    margin-left: unset;
    margin-right: 20px;
  }

  // makes table paginators nicer
  .pagination.is-simple {
    padding-right: 20px;
  }

  .dialog .modal-card-body ul {
    list-style: unset;
    padding: 0px 0px 0px 30px;
  }

  // Make the notification nicer
  .notices .snackbar {
    background: white;
    color: $grey-20-color;
    border: 1px solid $grey-85-color;
  }

  // Make selected table row not use primary color
  .table tr.is-selected {
    background-color: $grey-90-color;
    color: $grey-20-color;
  }
</style>
